import { useEffect, useState, useCallback } from "react";
import api, { formatApiError } from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { RefreshCw, AlertTriangle, AlertOctagon, Clock, Mail, ExternalLink } from "lucide-react";
import { toast } from "sonner";

function formatEUR(v) {
  return Number(v || 0).toLocaleString("it-IT", { style: "currency", currency: "EUR" });
}

function formatDate(iso) {
  if (!iso) return "—";
  try {
    return new Date(iso).toLocaleString("it-IT", {
      day: "2-digit", month: "2-digit", year: "numeric",
      hour: "2-digit", minute: "2-digit",
    });
  } catch { return iso; }
}

function computeDaysAgo(iso) {
  if (!iso) return null;
  const d = new Date(iso);
  const days = Math.floor((Date.now() - d.getTime()) / 86_400_000);
  return days;
}

const STATE_UI = {
  past_due_grace: {
    badge: "bg-amber-500/20 text-amber-300 border-amber-700",
    label: "In tolleranza",
    icon: Clock,
  },
  past_due_blocked: {
    badge: "bg-red-500/20 text-red-300 border-red-700",
    label: "Bloccata (tolleranza scaduta)",
    icon: AlertOctagon,
  },
  canceled: {
    badge: "bg-red-500/20 text-red-300 border-red-700",
    label: "Annullata",
    icon: AlertOctagon,
  },
  unpaid: {
    badge: "bg-red-500/20 text-red-300 border-red-700",
    label: "Non pagata",
    icon: AlertOctagon,
  },
  expired: {
    badge: "bg-slate-500/20 text-slate-300 border-slate-700",
    label: "Scaduta",
    icon: AlertTriangle,
  },
  pending: {
    badge: "bg-slate-500/20 text-slate-300 border-slate-700",
    label: "In attesa",
    icon: Clock,
  },
};

function classify(row) {
  const bs = row.billing_status || {};
  const ps = row.plan_status;
  if (ps === "past_due") return bs.in_grace_period ? "past_due_grace" : "past_due_blocked";
  if (STATE_UI[ps]) return ps;
  return "pending";
}

export default function MasterPagamenti() {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);

  const fetchRows = useCallback(async () => {
    setLoading(true);
    try {
      const r = await api.get("/master/billing/at-risk");
      setRows(r.data || []);
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchRows(); }, [fetchRows]);

  const summary = {
    total: rows.length,
    grace: rows.filter((r) => r.plan_status === "past_due" && r.billing_status?.in_grace_period).length,
    blocked: rows.filter((r) => r.billing_status?.blocked).length,
    mrrAtRisk: rows.reduce((s, r) => s + Number(r.effective_price_net || 0), 0),
  };

  return (
    <div className="max-w-6xl" data-testid="master-pagamenti">
      <div className="flex items-center justify-between mb-4">
        <div>
          <div className="text-[11px] uppercase tracking-widest text-amber-400 font-semibold">Master</div>
          <h1 className="font-heading text-2xl font-bold">Pagamenti a rischio</h1>
          <div className="text-sm text-slate-400 mt-1">
            Agenzie con rinnovo fallito o abbonamento non attivo. La tolleranza dopo un tentativo fallito è di 3 giorni.
          </div>
        </div>
        <Button
          variant="outline"
          size="sm"
          onClick={fetchRows}
          disabled={loading}
          className="border-slate-700 bg-slate-900 text-slate-200 hover:bg-slate-800"
          data-testid="master-pagamenti-refresh"
        >
          <RefreshCw className={`w-4 h-4 mr-2 ${loading ? "animate-spin" : ""}`} />
          Aggiorna
        </Button>
      </div>

      {/* Summary cards */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-5">
        <Card className="bg-slate-900 border-slate-800 p-4">
          <div className="text-xs uppercase text-slate-400">Totale</div>
          <div className="text-2xl font-bold text-slate-100 mt-1" data-testid="stat-total">{summary.total}</div>
        </Card>
        <Card className="bg-slate-900 border-amber-900/60 p-4">
          <div className="text-xs uppercase text-amber-300">In tolleranza (grace)</div>
          <div className="text-2xl font-bold text-amber-200 mt-1" data-testid="stat-grace">{summary.grace}</div>
        </Card>
        <Card className="bg-slate-900 border-red-900/60 p-4">
          <div className="text-xs uppercase text-red-300">Bloccate</div>
          <div className="text-2xl font-bold text-red-200 mt-1" data-testid="stat-blocked">{summary.blocked}</div>
        </Card>
        <Card className="bg-slate-900 border-slate-800 p-4">
          <div className="text-xs uppercase text-slate-400">MRR a rischio</div>
          <div className="text-2xl font-bold text-slate-100 mt-1" data-testid="stat-mrr">{formatEUR(summary.mrrAtRisk)}</div>
        </Card>
      </div>

      {/* Table */}
      <Card className="bg-slate-900 border-slate-800 overflow-hidden">
        {loading ? (
          <div className="p-8 text-center text-slate-400">Caricamento…</div>
        ) : rows.length === 0 ? (
          <div className="p-10 text-center text-slate-400">
            <div className="text-3xl mb-2">🎉</div>
            Nessuna agenzia con pagamento in ritardo o fallito.
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-slate-950/60 text-slate-400 uppercase text-[11px]">
                <tr>
                  <th className="text-left px-4 py-3">Agenzia</th>
                  <th className="text-left px-4 py-3">Piano</th>
                  <th className="text-left px-4 py-3">Stato</th>
                  <th className="text-left px-4 py-3">In ritardo da</th>
                  <th className="text-left px-4 py-3">Deadline / Info</th>
                  <th className="text-left px-4 py-3">Contatto</th>
                  <th className="text-right px-4 py-3">MRR</th>
                  <th className="text-center px-4 py-3">Reminder</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-800">
                {rows.map((r) => {
                  const cls = classify(r);
                  const ui = STATE_UI[cls];
                  const Icon = ui.icon;
                  const days = computeDaysAgo(r.past_due_since);
                  const graceEnd = r.billing_status?.grace_period_ends_at;
                  return (
                    <tr key={r.id} className="hover:bg-slate-800/40" data-testid={`pagamenti-row-${r.id}`}>
                      <td className="px-4 py-3">
                        <div className="font-semibold text-slate-100">{r.nome_agenzia || "—"}</div>
                        <div className="text-xs text-slate-500">
                          Creata: {formatDate(r.created_at)}
                        </div>
                      </td>
                      <td className="px-4 py-3 text-slate-300">{r.plan_name}</td>
                      <td className="px-4 py-3">
                        <Badge variant="outline" className={ui.badge}>
                          <Icon className="w-3 h-3 mr-1 inline" />
                          {ui.label}
                        </Badge>
                      </td>
                      <td className="px-4 py-3 text-slate-300">
                        {r.past_due_since ? (
                          <>
                            <div>{formatDate(r.past_due_since)}</div>
                            {days != null ? <div className="text-xs text-slate-500">{days} giorni fa</div> : null}
                          </>
                        ) : "—"}
                      </td>
                      <td className="px-4 py-3 text-slate-300">
                        {graceEnd ? (
                          <>
                            <div>{formatDate(graceEnd)}</div>
                            <div className="text-xs text-slate-500">Fine tolleranza</div>
                          </>
                        ) : r.plan_status === "canceled" ? (
                          <span className="text-xs text-slate-500">
                            {r.cancel_at_period_end ? "Cancellazione richiesta" : "Sub cancellata"}
                          </span>
                        ) : "—"}
                      </td>
                      <td className="px-4 py-3">
                        {r.owner?.email ? (
                          <a
                            href={`mailto:${r.owner.email}?subject=Pagamento%20Incassi%20Cloud`}
                            className="inline-flex items-center gap-1 text-amber-300 hover:text-amber-200 text-xs"
                            data-testid={`pagamenti-email-${r.id}`}
                          >
                            <Mail className="w-3.5 h-3.5" />
                            {r.owner.email}
                            <ExternalLink className="w-3 h-3 opacity-60" />
                          </a>
                        ) : (
                          <span className="text-xs text-slate-500">—</span>
                        )}
                        <div className="text-xs text-slate-500 mt-0.5">
                          {(r.owner?.nome || "") + " " + (r.owner?.cognome || "")}
                        </div>
                      </td>
                      <td className="px-4 py-3 text-right text-slate-200">{formatEUR(r.effective_price_net)}</td>
                      <td className="px-4 py-3 text-center">
                        {r.past_due_reminder_sent ? (
                          <Badge variant="outline" className="bg-slate-800 text-slate-300 border-slate-700 text-[10px]">
                            Inviato
                          </Badge>
                        ) : cls === "past_due_grace" ? (
                          <span className="text-xs text-slate-500">Prossimo</span>
                        ) : (
                          <span className="text-xs text-slate-600">—</span>
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </Card>

      <div className="mt-4 text-xs text-slate-500">
        Un reminder automatico via email viene inviato all&apos;agente ~24h prima della fine del periodo di tolleranza (3 giorni).
      </div>
    </div>
  );
}
